'use client';

import { useEffect, useState } from 'react';

type Outcome = 'accepted' | 'dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<{ outcome: Outcome }>;
  userChoice: Promise<{ outcome: Outcome }>;
}

const STORAGE_KEY = 'kontora_install_prompt_dismissed_v1' as const;

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = (e: Event) => {
      e.preventDefault();
      if (window.localStorage.getItem(STORAGE_KEY)) return;
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function handleInstall() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      // eslint-disable-next-line no-console
      console.info('[pwa] User hat Installation akzeptiert');
    }
    setIsVisible(false);
    setDeferredPrompt(null);
  }

  function handleDismiss() {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, '1');
    }
    setIsVisible(false);
  }

  if (!isVisible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="install-prompt-title"
      data-tour="pwa-install-prompt"
      className="fixed bottom-4 left-4 z-[60] flex max-w-sm items-start gap-3 rounded-[var(--k-radius-md)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] p-3 shadow-[var(--k-shadow-md)]"
    >
      <span aria-hidden="true" className="text-2xl">📱</span>
      <div className="flex-1">
        <p id="install-prompt-title" className="text-sm font-semibold">Kontora installieren</p>
        <p className="mt-0.5 text-xs text-[var(--k-color-text-secondary)]">
          Als App auf deinem Gerät installieren für schnellen Zugriff.
        </p>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={handleInstall}
            data-tour="pwa-install-confirm"
            className="rounded-md bg-[var(--k-color-accent)] px-3 py-1 text-xs font-bold text-white"
          >
            Installieren
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded-md px-3 py-1 text-xs font-semibold text-[var(--k-color-text-secondary)] hover:underline"
          >
            Später
          </button>
        </div>
      </div>
    </div>
  );
}
